import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import productService from "../services/productService";
import categoryService from "../services/categoryService";
import attributeService from "../services/attributeService";
import categoryAttributeService from "../services/categoryAttributeService";

/**
 * Custom hook for managing product catalog
 * Handles product data fetching, filtering, sorting, and pagination
 * @returns {Object} Product catalog state and handlers
 */
const useProductCatalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("hooks");

  // State for products and loading
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // State for categories
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryHierarchy, setCategoryHierarchy] = useState({});

  // State for filters
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || ""
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState(
    searchParams.get("subcategory") || ""
  );
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "newest");
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1", 10)
  );
  const [limit, setLimit] = useState(12);

  // Initialize price range from URL if available
  const urlMinPrice = searchParams.get("minPrice");
  const urlMaxPrice = searchParams.get("maxPrice");
  const hasUrlPriceFilter = urlMinPrice || urlMaxPrice;

  const [priceRange, setPriceRange] = useState(
    hasUrlPriceFilter
      ? [
          urlMinPrice ? parseFloat(urlMinPrice) : 0,
          urlMaxPrice ? parseFloat(urlMaxPrice) : 10000,
        ]
      : [0, 10000]
  );
  const [minMaxPrice, setMinMaxPrice] = useState([0, 10000]);
  const [userSetPriceRange, setUserSetPriceRange] = useState(hasUrlPriceFilter);

  // Initialize weight range from URL if available
  const urlMinWeight = searchParams.get("minWeight");
  const urlMaxWeight = searchParams.get("maxWeight");
  const hasUrlWeightFilter = urlMinWeight || urlMaxWeight;

  const [weightRange, setWeightRange] = useState(
    hasUrlWeightFilter
      ? [
          urlMinWeight ? parseFloat(urlMinWeight) : 0,
          urlMaxWeight ? parseFloat(urlMaxWeight) : 10000,
        ]
      : [0, 10000]
  );
  const [minMaxWeight, setMinMaxWeight] = useState([0, 10000]);
  const [userSetWeightRange, setUserSetWeightRange] = useState(hasUrlWeightFilter);

  // State for attribute filters
  const [attributeFilters, setAttributeFilters] = useState({});
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);

  // State for mobile filter drawer
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch products when filters change
  useEffect(() => {
    console.log("Filter changed, triggering product fetch:");
    console.log({
      searchTerm,
      selectedCategory,
      selectedSubcategory,
      sortBy,
      page,
      priceRange: `$${priceRange[0]}-$${priceRange[1]}`,
      userSetPriceRange,
      weightRange: `${weightRange[0]}ct-${weightRange[1]}ct`,
      userSetWeightRange,
      attributeFilters: Object.keys(attributeFilters).length,
    });

    fetchProducts();

    // Update URL search params
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (selectedCategory) params.set("category", selectedCategory);
    if (selectedSubcategory) params.set("subcategory", selectedSubcategory);
    if (sortBy) params.set("sort", sortBy);
    if (page > 1) params.set("page", page.toString());

    // Add price range to URL if user has set it
    if (userSetPriceRange) {
      params.set("minPrice", priceRange[0].toString());
      params.set("maxPrice", priceRange[1].toString());
    }

    // Add weight range to URL if user has set it
    if (userSetWeightRange) {
      params.set("minWeight", weightRange[0].toString());
      params.set("maxWeight", weightRange[1].toString());
    }

    // Add attribute filters to URL
    Object.entries(attributeFilters).forEach(([key, value]) => {
      if (value) params.set(`attr_${key}`, value);
    });

    setSearchParams(params);
  }, [
    searchTerm,
    selectedCategory,
    selectedSubcategory,
    sortBy,
    page,
    // Only trigger a fetch when price range changes in a meaningful way
    // Convert to string representation to avoid unnecessary re-renders
    JSON.stringify(priceRange),
    userSetPriceRange,
    JSON.stringify(weightRange),
    userSetWeightRange,
    // Convert filters to string rep for same reason
    JSON.stringify(attributeFilters),
  ]);

  // Fetch category attributes when category changes
  useEffect(() => {
    console.log("Category/subcategory dependency changed:");
    console.log(`selectedCategory: ${selectedCategory}`);
    console.log(`selectedSubcategory: ${selectedSubcategory}`);

    // When either category or subcategory changes, fetch the appropriate attributes
    if (selectedSubcategory) {
      console.log(`Using subcategory for attributes: ${selectedSubcategory}`);
      fetchCategoryAttributes(selectedSubcategory);
    } else if (selectedCategory) {
      console.log(`Using category for attributes: ${selectedCategory}`);
      fetchCategoryAttributes(selectedCategory);
    } else {
      // No category selected — show globally filterable attributes (e.g. Cut)
      // so users can filter without having to pick a category first
      console.log("No category selected, fetching globally filterable attributes");
      fetchGlobalFilterableAttributes();
    }
  }, [selectedCategory, selectedSubcategory]);

  /**
   * Fetch all filterable attributes (used when no category is selected)
   */
  const fetchGlobalFilterableAttributes = async () => {
    setLoadingAttributes(true);
    try {
      setAttributeFilters({});
      const response = await attributeService.getAttributes();
      const filterable = (response || []).filter((a) => a.isFilterable);
      setAvailableAttributes(filterable);
    } catch (error) {
      console.error("Error fetching global attributes:", error);
      setAvailableAttributes([]);
    } finally {
      setLoadingAttributes(false);
    }
  };

  /**
   * Fetch categories from the API
   */
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await categoryService.getCategories({
        includeInactive: false,
      });
      setCategories(data);

      // Build category hierarchy for efficient lookup
      const hierarchy = {};
      data.forEach((category) => {
        if (!hierarchy[category.id]) {
          hierarchy[category.id] = {
            ...category,
            directChildren: [],
            allDescendants: [],
            subcategories: data.filter((c) => c.parentId === category.id),
          };
        }

        if (category.parentId) {
          if (!hierarchy[category.parentId]) {
            hierarchy[category.parentId] = {
              directChildren: [],
              allDescendants: [],
              subcategories: [],
            };
          }

          // Add as direct child
          hierarchy[category.parentId].directChildren.push(category.id);

          // Add to all descendants
          hierarchy[category.parentId].allDescendants.push(category.id);

          // Also add this category's descendants to parent's all descendants
          if (
            hierarchy[category.id] &&
            hierarchy[category.id].allDescendants.length > 0
          ) {
            hierarchy[category.parentId].allDescendants.push(
              ...hierarchy[category.id].allDescendants
            );
          }
        }
      });

      console.log("Category hierarchy:", hierarchy);
      setCategoryHierarchy(hierarchy);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  /**
   * Fetch attributes for a category
   * @param {string} categoryId - Category ID
   */
  const fetchCategoryAttributes = async (categoryId) => {
    if (!categoryId) {
      console.log("No category ID provided, resetting attributes");
      setAvailableAttributes([]);
      setAttributeFilters({});
      return;
    }

    setLoadingAttributes(true);
    console.log(`Fetching attributes for category: ${categoryId}`);

    try {
      // First reset attribute filters to avoid stale filters
      console.log("Resetting attribute filters before fetch");
      setAttributeFilters({});

      const response = await categoryAttributeService.getCategoryAttributes(
        categoryId
      );

      console.log(
        `Fetched ${response.length} attributes for category ${categoryId}`
      );

      setAvailableAttributes(response);
    } catch (error) {
      console.error("Error fetching category attributes:", error);
      setAvailableAttributes([]);
    } finally {
      setLoadingAttributes(false);
    }
  };

  /**
   * Fetch products from the API
   */
  const fetchProducts = async () => {
    try {
      setLoading(true);

      // Build filter params
      const params = {
        page,
        limit,
        search: searchTerm,
        sortBy: getSortField(),
        sortOrder: getSortOrder(),
      };

      // Add price range filters - only if user has adjusted them from the full available range
      if (
        priceRange &&
        priceRange.length === 2 &&
        minMaxPrice &&
        minMaxPrice.length === 2
      ) {
        // Check if user has actually filtered the price range
        const hasMinPriceFilter = priceRange[0] > minMaxPrice[0];
        const hasMaxPriceFilter = priceRange[1] < minMaxPrice[1];

        // Only add filter params when there's an actual filter
        if (hasMinPriceFilter) {
          params.minPrice = priceRange[0];
          console.log(`Applying minimum price filter: $${priceRange[0]}`);
        }

        if (hasMaxPriceFilter) {
          params.maxPrice = priceRange[1];
          console.log(`Applying maximum price filter: $${priceRange[1]}`);
        }

        if (hasMinPriceFilter || hasMaxPriceFilter) {
          console.log(
            `Using price range filter: $${priceRange[0]}-$${priceRange[1]}`
          );
        }
      }

      // Add weight range filters - only if user has adjusted them from the full available range
      if (
        weightRange &&
        weightRange.length === 2 &&
        minMaxWeight &&
        minMaxWeight.length === 2
      ) {
        const hasMinWeightFilter = weightRange[0] > minMaxWeight[0];
        const hasMaxWeightFilter = weightRange[1] < minMaxWeight[1];

        if (hasMinWeightFilter) {
          params.minWeight = weightRange[0];
          console.log(`Applying minimum weight filter: ${weightRange[0]}ct`);
        }

        if (hasMaxWeightFilter) {
          params.maxWeight = weightRange[1];
          console.log(`Applying maximum weight filter: ${weightRange[1]}ct`);
        }
      }

      // Add category filters
      if (selectedSubcategory) {
        console.log(`Using subcategory filter: ${selectedSubcategory}`);
        params.category = selectedSubcategory; // Backend expects 'category' parameter
      } else if (selectedCategory) {
        console.log(`Using category filter: ${selectedCategory}`);
        params.category = selectedCategory; // Backend expects 'category' parameter
      }

      // Add attribute filters - only include non-empty values
      const attributeFilterParams = {};
      Object.entries(attributeFilters).forEach(([key, value]) => {
        if (value && value.trim() !== "") {
          attributeFilterParams[key] = value;
        }
      });

      if (Object.keys(attributeFilterParams).length > 0) {
        // Format as JSON string for proper API parameter passing
        params.attributes = JSON.stringify(attributeFilterParams);
      }

      console.log("API params:", params);

      const data = await productService.getProducts(params);
      console.log("API response:", data);

      // Get products from the API response - no need for heavy processing
      const productsFromApi = Array.isArray(data.products) ? data.products : [];

      console.log(
        `Received ${productsFromApi.length} products from optimized API`
      );

      // The backend now provides variation summaries, so no heavy processing needed
      setProducts(productsFromApi);

      // Get pagination info directly from the API response
      if (data.pagination) {
        setTotalProducts(data.pagination.totalItems || productsFromApi.length);
        setTotalPages(
          data.pagination.totalPages ||
            Math.ceil(productsFromApi.length / limit) ||
            1
        );
      } else {
        setTotalProducts(productsFromApi.length);
        setTotalPages(Math.ceil(productsFromApi.length / limit) || 1);
      }

      // Handle price range updates from the backend
      if (
        data.priceRange &&
        data.priceRange.min !== undefined &&
        data.priceRange.max !== undefined
      ) {
        const newMinMax = [data.priceRange.min, data.priceRange.max];
        console.log("Received price range from API:", newMinMax);

        // Always update minMaxPrice with the backend's values
        if (
          minMaxPrice[0] !== newMinMax[0] ||
          minMaxPrice[1] !== newMinMax[1]
        ) {
          console.log("Updating minMaxPrice bounds to:", newMinMax);
          setMinMaxPrice(newMinMax);
        }

        // Price range logic remains the same
        const isInitialLoad = priceRange[0] === 0 && priceRange[1] === 10000;
        const categoryChanged =
          selectedCategory !== searchParams.get("category") ||
          selectedSubcategory !== searchParams.get("subcategory");
        const isCompletelyInvalid =
          priceRange[1] < newMinMax[0] || priceRange[0] > newMinMax[1];

        if (
          isInitialLoad ||
          (categoryChanged && !userSetPriceRange) ||
          isCompletelyInvalid
        ) {
          console.log(
            "Resetting price range filter to available range:",
            newMinMax
          );
          setPriceRange(newMinMax);
          if (isCompletelyInvalid && userSetPriceRange) {
            setUserSetPriceRange(false);
          }
        } else if (
          userSetPriceRange &&
          (priceRange[0] < newMinMax[0] || priceRange[1] > newMinMax[1])
        ) {
          const adjustedRange = [
            Math.max(priceRange[0], newMinMax[0]),
            Math.min(priceRange[1], newMinMax[1]),
          ];
          console.log(
            "Adjusting user's price range to fit within new bounds:",
            adjustedRange
          );
          if (
            priceRange[0] !== adjustedRange[0] ||
            priceRange[1] !== adjustedRange[1]
          ) {
            setPriceRange(adjustedRange);
          }
        } else if (userSetPriceRange) {
          console.log("Preserving user's selected price range:", priceRange);
        } else {
          console.log("Using backend's price range:", newMinMax);
          setPriceRange(newMinMax);
        }
      }

      // Handle weight range updates from the backend
      if (
        data.weightRange &&
        data.weightRange.min !== undefined &&
        data.weightRange.max !== undefined
      ) {
        const newMinMaxWeight = [data.weightRange.min, data.weightRange.max];
        console.log("Received weight range from API:", newMinMaxWeight);

        if (
          minMaxWeight[0] !== newMinMaxWeight[0] ||
          minMaxWeight[1] !== newMinMaxWeight[1]
        ) {
          setMinMaxWeight(newMinMaxWeight);
        }

        const isInitialWeightLoad = weightRange[0] === 0 && weightRange[1] === 10000;
        const weightCategoryChanged =
          selectedCategory !== searchParams.get("category") ||
          selectedSubcategory !== searchParams.get("subcategory");
        const isWeightCompletelyInvalid =
          weightRange[1] < newMinMaxWeight[0] || weightRange[0] > newMinMaxWeight[1];

        if (
          isInitialWeightLoad ||
          (weightCategoryChanged && !userSetWeightRange) ||
          isWeightCompletelyInvalid
        ) {
          setWeightRange(newMinMaxWeight);
          if (isWeightCompletelyInvalid && userSetWeightRange) {
            setUserSetWeightRange(false);
          }
        } else if (
          userSetWeightRange &&
          (weightRange[0] < newMinMaxWeight[0] || weightRange[1] > newMinMaxWeight[1])
        ) {
          const adjustedRange = [
            Math.max(weightRange[0], newMinMaxWeight[0]),
            Math.min(weightRange[1], newMinMaxWeight[1]),
          ];
          if (
            weightRange[0] !== adjustedRange[0] ||
            weightRange[1] !== adjustedRange[1]
          ) {
            setWeightRange(adjustedRange);
          }
        } else if (!userSetWeightRange) {
          setWeightRange(newMinMaxWeight);
        }
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get sort field based on sortBy value
   * @returns {string} Sort field
   */
  const getSortField = () => {
    switch (sortBy) {
      case "price-low":
      case "price-high":
        return "price";
      case "rating":
        return "rating";
      case "newest":
      default:
        return "createdAt";
    }
  };

  /**
   * Get sort order based on sortBy value
   * @returns {string} Sort order
   */
  const getSortOrder = () => {
    switch (sortBy) {
      case "price-low":
        return "ASC";
      case "price-high":
      case "rating":
      case "newest":
      default:
        return "DESC";
    }
  };

  /**
   * Handle search input change
   * @param {Event} event - Input change event
   */
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  /**
   * Handle category selection change
   * @param {Event} event - Select change event
   */
  const handleCategoryChange = (event) => {
    const categoryId = event.target.value;

    // Skip if the value hasn't actually changed
    if (selectedCategory === categoryId) {
      console.log("Category selection unchanged, skipping update");
      return;
    }

    console.log("Category changed to:", categoryId);

    // Reset related state
    setAttributeFilters({}); // Clear attribute filters
    setSelectedSubcategory(""); // Clear subcategory selection

    // Don't set price range directly - let the fetched data update it
    // to avoid multiple state updates causing unnecessary renders

    // Reset page
    setPage(1);

    // Update category selection last
    setSelectedCategory(categoryId);
  };

  /**
   * Handle subcategory selection change
   * @param {Event} event - Select change event
   */
  const handleSubcategoryChange = (event) => {
    const subcategoryId = event.target.value;

    // Skip if the value hasn't actually changed
    if (selectedSubcategory === subcategoryId) {
      console.log("Subcategory selection unchanged, skipping update");
      return;
    }

    console.log("Subcategory changed to:", subcategoryId);

    // Reset related state
    setAttributeFilters({}); // Clear attribute filters

    // Don't set price range directly - let the fetched data update it
    // to avoid multiple state updates causing unnecessary renders

    // Reset page
    setPage(1);

    // Update subcategory selection last
    setSelectedSubcategory(subcategoryId);
  };

  /**
   * Handle sort selection change
   * @param {Event} event - Select change event
   */
  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  /**
   * Handle pagination change
   * @param {Event} event - Pagination change event
   * @param {number} value - New page number
   */
  const handlePageChange = (event, value) => {
    setPage(value);

    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /**
   * Handle price range change
   * @param {Event} event - Slider change event
   * @param {Array} newValue - New price range
   */
  const handlePriceRangeChange = (event, newValue) => {
    console.log(
      "Price range change requested from:",
      priceRange,
      "to:",
      newValue
    );

    // Skip if values haven't changed to avoid unnecessary renders
    if (priceRange[0] === newValue[0] && priceRange[1] === newValue[1]) {
      console.log("No change in price range, skipping update");
      return;
    }

    // Ensure the values are within the bounds of available prices
    const validValues = [
      Math.max(newValue[0], minMaxPrice[0] || 0),
      Math.min(newValue[1], minMaxPrice[1] || 10000),
    ];

    // Check if we need to adjust the range
    if (validValues[0] !== newValue[0] || validValues[1] !== newValue[1]) {
      console.log("Adjusted price range to fit available bounds:", validValues);
    }

    // Flag that user has manually set the price range
    setUserSetPriceRange(true);

    // Update the price range state - this triggers a product fetch via useEffect
    console.log("Setting user-selected price range to:", validValues);
    setPriceRange(validValues);

    // Reset to page 1 when changing filters
    setPage(1);
  };

  /**
   * Handle weight range change
   * @param {Event} event - Slider change event
   * @param {Array} newValue - New weight range
   */
  const handleWeightRangeChange = (event, newValue) => {
    // Skip if values haven't changed
    if (weightRange[0] === newValue[0] && weightRange[1] === newValue[1]) {
      return;
    }

    // Ensure the values are within the bounds
    const validValues = [
      Math.max(newValue[0], minMaxWeight[0] || 0),
      Math.min(newValue[1], minMaxWeight[1] || 10000),
    ];

    setUserSetWeightRange(true);
    setWeightRange(validValues);

    setPage(1);
  };

  /**
   * Handle attribute filter change
   * @param {string} attributeId - The attribute id
   * @param {string} value - The attribute value
   */
  const handleAttributeFilterChange = (attributeId, value) => {
    console.log(`Setting attribute filter ${attributeId} to value: ${value}`);

    // Update the attribute filters
    const updatedFilters = { ...attributeFilters };

    if (value && value.trim() !== "") {
      updatedFilters[attributeId] = value;
    } else {
      // If value is empty, remove this filter completely
      delete updatedFilters[attributeId];
    }

    console.log("Updated attribute filters:", updatedFilters);

    // Reset to page 1 when changing filters
    setPage(1);
    setAttributeFilters(updatedFilters);
  };

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    console.log("Clearing all filters");

    // Batch our state updates to reduce renders
    const updates = [];

    // Reset category only if it's set
    if (selectedCategory) {
      updates.push(() => setSelectedCategory(""));
    }

    // Reset subcategory only if it's set
    if (selectedSubcategory) {
      updates.push(() => setSelectedSubcategory(""));
    }

    // Reset price range to current min/max values only if it's different
    if (priceRange[0] !== minMaxPrice[0] || priceRange[1] !== minMaxPrice[1]) {
      updates.push(() => setPriceRange(minMaxPrice));
    }

    // Reset user set price range flag
    if (userSetPriceRange) {
      updates.push(() => setUserSetPriceRange(false));
    }

    // Reset weight range to current min/max values only if it's different
    if (weightRange[0] !== minMaxWeight[0] || weightRange[1] !== minMaxWeight[1]) {
      updates.push(() => setWeightRange(minMaxWeight));
    }

    // Reset user set weight range flag
    if (userSetWeightRange) {
      updates.push(() => setUserSetWeightRange(false));
    }

    // Reset attribute filters only if there are any
    if (Object.keys(attributeFilters).length > 0) {
      updates.push(() => setAttributeFilters({}));
    }

    // Reset page only if not on page 1
    if (page !== 1) {
      updates.push(() => setPage(1));
    }

    // Apply all updates
    updates.forEach((update) => update());

    console.log("Filters cleared");
  };

  /**
   * Toggle filter drawer
   */
  const toggleFilterDrawer = () => {
    setFilterDrawerOpen(!filterDrawerOpen);
  };

  return {
    // State
    products,
    loading,
    totalProducts,
    totalPages,
    categories,
    loadingCategories,
    categoryHierarchy,
    searchTerm,
    selectedCategory,
    selectedSubcategory,
    sortBy,
    page,
    limit,
    priceRange,
    minMaxPrice,
    weightRange,
    minMaxWeight,
    attributeFilters,
    availableAttributes,
    loadingAttributes,
    filterDrawerOpen,
    userSetPriceRange,
    userSetWeightRange,

    // Handlers
    handleSearchChange,
    handleCategoryChange,
    handleSubcategoryChange,
    handleSortChange,
    handlePageChange,
    handlePriceRangeChange,
    handleWeightRangeChange,
    handleAttributeFilterChange,
    clearFilters,
    toggleFilterDrawer,
    setUserSetPriceRange,
    setPriceRange,
    setUserSetWeightRange,
    setWeightRange,
  };
};

export default useProductCatalog;
